import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core';
import { useMutation } from '@apollo/client';
import Radio from '@material-ui/core/Radio';
import router from 'next/router';

import {
  PAY_ORDER_CHARGE_MUTATION,
  PAY_ORDER_CHECKOUT_MUTATION
} from '../../../BuyProduct/Step/graphql';
import { StripeConfirmCardPayment } from '../../../StripeConfirmCardPayment';
import { usePaymentCompletion } from '../../../AsyncProcessCompletion';
import { formatCurrency } from '../../../../components/FormattedCurrency';
import { StripeCheckoutRedirect } from '../../../StripeCheckoutRedirect';
import { useCurrentUser } from '../../../../lib/core/useCurrentUser';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useWizardContext } from '../../../../components/Wizard';
import { useToasts } from '../../../../components/Toasts';
import FormControl from '@material-ui/core/FormControl';
import { pushRoute } from '../../../../components/Link';
import { TOP_UP_WALLET_MUTATION } from '../../graphql';
import {
  Alert,
  Box,
  Flex,
  H1,
  LoadableButton
} from '@urbaninfrastructure/react-ui-kit';
import {
  CurrentUser,
  PaymentDocumentType,
  PaymentMethodType,
  payOrderChargeMutation,
  payOrderChargeMutation_payOrderCharge_ConfirmationSessionStripe,
  payOrderChargeMutationVariables,
  payOrderCheckoutMutation,
  payOrderCheckoutMutation_payOrderCheckout_CheckoutSessionStripe,
  payOrderCheckoutMutationVariables,
  placeWalletDepositOrder,
  placeWalletDepositOrderVariables
} from '../../../../core-types';
import { useSystem } from '../../../../lib/core/useSystem';
import { PaymentMethodTypeList } from '../../../../components/PaymentMethodTypeList';
import { Loading } from '../../../../components';

type Props = {
  goNext: () => void;
  currentUser: CurrentUser;
};

function getPaths({
  asPath
}: {
  asPath: string;
  token: string;
  type: PaymentDocumentType;
}) {
  return {
    successPath: `/profile/wallet`,
    cancelPath: asPath
  };
}

const useStyles = makeStyles(() => ({
  radio: {
    '&.MuiRadio-colorSecondary.Mui-checked': {
      color: 'black'
    }
  }
}));

export const TopUpWallet = ({ goNext, currentUser }: Props) => {
  const { addToast } = useToasts();
  const classes = useStyles();
  const intl = useIntl();
  const system = useSystem();
  const [value, setValue] = useState('5');
  const [
    paymentMethodType,
    setPaymentMethodType
  ] = useState<PaymentMethodType | null>(null);
  const [
    confirmationSessionStripeError,
    setConfirmationSessionStripeError
  ] = useState<boolean>(false);
  const [chargePendingToken, setChargePendingToken] = useState<
    string | undefined
  >();
  const { refetch } = useCurrentUser();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  const [checkoutSessionStripe, setCheckoutSessionStripe] = useState<
    payOrderCheckoutMutation_payOrderCheckout_CheckoutSessionStripe | undefined
  >();

  const [
    confirmationSessionStripeData,
    setConfirmationSessionStripeData
  ] = useState<
    payOrderChargeMutation_payOrderCharge_ConfirmationSessionStripe | undefined
  >();

  const hasMultiplePaymentMethodTypes =
    system.availablePaymentMethods.length > 1;

  const [payOrderCharge, { loading, error }] = useMutation<
    payOrderChargeMutation,
    payOrderChargeMutationVariables
  >(PAY_ORDER_CHARGE_MUTATION, {
    onCompleted: data => {
      switch (data?.payOrderCharge.__typename) {
        case 'PaymentIntentError': {
          addToast({
            text: data.payOrderCharge.description,
            type: 'error'
          });
          break;
        }
        case 'ChargeCompleted': {
          pushRoute({
            intl,
            route: '/profile/wallet'
          });
          break;
        }
        case 'ChargePending': {
          setChargePendingToken(data.payOrderCharge.paymentDocument.token);
          break;
        }
        case 'ConfirmationSessionStripe': {
          setConfirmationSessionStripeData(data.payOrderCharge);
          break;
        }
        default: {
          break;
        }
      }
      refetch();
      goNext();
    },
    onError: () => {
      addToast({
        text: error?.message,
        type: 'error'
      });
    }
  });

  const [payOrderCheckout, payOrderCheckoutResult] = useMutation<
    payOrderCheckoutMutation,
    payOrderCheckoutMutationVariables
  >(PAY_ORDER_CHECKOUT_MUTATION, {
    onCompleted: data => {
      switch (data.payOrderCheckout.__typename) {
        case 'CheckoutSessionStripe': {
          setCheckoutSessionStripe(data.payOrderCheckout);
          break;
        }
        case 'CheckoutSessionPayex':
        case 'CheckoutSessionNevogate': {
          window.location.replace(data.payOrderCheckout.redirectUrl);
          break;
        }
        default: {
          break;
        }
      }
    },
    onError: () => {
      addToast({
        text: payOrderCheckoutResult.error?.message,
        type: 'error'
      });
    }
  });

  const [placeWalletDepositOrder, placeWalletDepositOrderResult] = useMutation<
    placeWalletDepositOrder,
    placeWalletDepositOrderVariables
  >(TOP_UP_WALLET_MUTATION, {
    onCompleted: data => {
      switch (data.placeWalletDepositOrder.__typename) {
        case 'Order': {
          const { paymentMethod } = currentUser;
          const useCheckout =
            !paymentMethod ||
            (hasMultiplePaymentMethodTypes &&
              paymentMethodType !== PaymentMethodType.card);

          return useCheckout
            ? payOrderCheckout({
                variables: {
                  orderId: data.placeWalletDepositOrder.id,
                  paths: getPaths({
                    asPath: router.asPath,
                    token: data.placeWalletDepositOrder.token,
                    type: PaymentDocumentType.order
                  }),
                  autoRenewProduct: false,
                  paymentMethodType
                }
              })
            : payOrderCharge({
                variables: {
                  orderId: data.placeWalletDepositOrder.id,
                  autoRenewProduct: false
                }
              });
        }
        case 'WalletDepositOrderError': {
          addToast({
            text: data.placeWalletDepositOrder.description,
            type: 'error'
          });
        }
      }
    },
    onError: () => {
      addToast({
        text: placeWalletDepositOrderResult.error?.message,
        type: 'error'
      });
    }
  });

  const chargePendingPolling = usePaymentCompletion({
    token: chargePendingToken,
    type: PaymentDocumentType.order,
    paymentState: 'success',
    skip: !chargePendingToken,
    onAsyncProcessCompleted: state => {
      switch (state) {
        case 'success': {
          refetch();
          pushRoute({ intl, route: '/profile/wallet' });
          break;
        }
        case 'error':
        case 'cancelled':
        case 'failed': {
          setChargePendingToken(undefined);
          addToast({
            text: 'Could not process payment.',
            type: 'error'
          });
          break;
        }
      }
    }
  });

  const chargePendingLoading =
    chargePendingPolling.loading ||
    chargePendingPolling.postMessageState === 'success';

  const handleTopUpWalletClick = useCallback(
    () =>
      placeWalletDepositOrder({
        variables: {
          amount: parseFloat(value)
        }
      }),
    [placeWalletDepositOrder, value]
  );

  if (!currentUser) {
    return null;
  }

  if (chargePendingLoading) {
    return (
      <Flex alignItems="center" justifyContent="center">
        <Loading />
        <FormattedMessage
          id="paymentProcessing"
          defaultMessage="Processing payment…"
        />
      </Flex>
    );
  }

  return (
    <Box>
      {checkoutSessionStripe && (
        <StripeCheckoutRedirect
          sessionId={checkoutSessionStripe.sessionId}
          publishableKey={checkoutSessionStripe.publishableKey}
          showSpinner={false}
        />
      )}
      {confirmationSessionStripeData && (
        <StripeConfirmCardPayment
          showSpinner={false}
          clientSecret={confirmationSessionStripeData.clientSecret}
          paymentMethod={confirmationSessionStripeData.paymentMethodRef}
          token={confirmationSessionStripeData.paymentDocument.token}
          type={PaymentDocumentType.order}
          publishableKey={confirmationSessionStripeData.publishableKey}
          onError={() => {
            setConfirmationSessionStripeError(true);
            setConfirmationSessionStripeData(undefined);
          }}
          onAsyncProcessCompleted={state => {
            switch (state) {
              case 'success': {
                setConfirmationSessionStripeError(false);
                refetch();
                pushRoute({ intl, route: 'profile/wallet' });
                break;
              }
              case 'error':
              case 'cancelled':
              case 'failed': {
                setConfirmationSessionStripeData(undefined);
                setConfirmationSessionStripeError(true);
                break;
              }
            }
          }}
        />
      )}
      {confirmationSessionStripeError && (
        <Alert variant="error">
          <FormattedMessage
            id="StripeCheckout.confirmationSessionStripe.error"
            defaultMessage="Could not confirm your payment card. Please try again or add a new payment card."
          />
        </Alert>
      )}
      <H1 typoStyle="lg" style={{ maxWidth: '600px' }}>
        {' '}
        <FormattedMessage
          id="modules.Profile.Wallet.chooseTopup"
          defaultMessage={'Choose how much to top up with:'}
        />
      </H1>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="initialTopUpAmount"
          name="initialTopUpAmount"
          value={value}
          onChange={handleChange}
        >
          <FormControlLabel
            value="5"
            control={<Radio className={classes.radio} />}
            label={formatCurrency({ value: 5, intl })}
          />
          <FormControlLabel
            value="10"
            control={<Radio className={classes.radio} />}
            label={formatCurrency({ value: 10, intl })}
          />
          <FormControlLabel
            value="20"
            control={<Radio className={classes.radio} />}
            label={formatCurrency({ value: 20, intl })}
          />
          <FormControlLabel
            value="50"
            control={<Radio className={classes.radio} />}
            label={formatCurrency({ value: 50, intl })}
          />
        </RadioGroup>
      </FormControl>
      {hasMultiplePaymentMethodTypes && (
        <Box mt="xxs" mb="xxs">
          <PaymentMethodTypeList
            selected={paymentMethodType}
            availablePaymentMethods={system.availablePaymentMethods}
            onSelect={setPaymentMethodType}
          />
        </Box>
      )}
      <Box>
        <LoadableButton
          shape="cool"
          variant="primary"
          mb="xs"
          mt="xs"
          loading={loading}
          onClick={handleTopUpWalletClick}
          disabled={hasMultiplePaymentMethodTypes && !paymentMethodType}
        >
          <FormattedMessage
            id="modules.Profile.Wallet.topupAmountNow"
            defaultMessage="Top up {0} now"
            values={{
              0: formatCurrency({ value: parseFloat(value), intl })
            }}
          />
        </LoadableButton>
      </Box>
    </Box>
  );
};

export function TopUpWalletStep({ currentUser }: { currentUser: CurrentUser }) {
  const { goNext } = useWizardContext();
  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center">
      <TopUpWallet goNext={goNext} currentUser={currentUser} />
    </Flex>
  );
}
